import { Col, Row } from 'react-bootstrap';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import CheckboxField from 'components/forms/checkboxField/CheckboxField';
import PropertySelectField from 'components/forms/domain/property/PropertySelectField';
import SelectField from 'components/forms/selectField/SelectField';
import TextField from 'components/forms/textField/TextField';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { Property } from 'models/Properties';
import { Channel } from 'models/Channels';
import { ICalType } from 'models/ICal';
import { CalendarImportFieldsContainer } from './CalendarImportSection.styles';
import { providerToLabelMap } from './CalendarImport.constants';

const channelToProviderMap = {
  [Channel.airbnb]: {
    label: providerToLabelMap[ICalType.AIRBNB],
    value: ICalType.AIRBNB,
  },
  [Channel.booking_com]: {
    label: providerToLabelMap[ICalType.BOOKING_COM],
    value: ICalType.BOOKING_COM,
  },
  [Channel.homeaway]: {
    label: providerToLabelMap[ICalType.VRBO],
    value: ICalType.VRBO,
  },
  [Channel.tripadvisor]: {
    label: providerToLabelMap[ICalType.TRIP_ADVISOR],
    value: ICalType.TRIP_ADVISOR,
  },
};

const channelToProviderArray = Object.keys(channelToProviderMap).map((key) => {
  const item = channelToProviderMap[key as keyof typeof channelToProviderMap];
  return {
    label: item.label,
    value: item.value,
  };
});

const CalendarImportFields = ({
  isFetchingProperties,
  allProperties,
  setPropertyUid,
}: {
  isFetchingProperties: boolean;
  allProperties: Property[];
  setPropertyUid: (propertyUid: string) => void;
}) => {
  const { t } = useTranslation();
  const property = useWatch({ name: 'property' });
  const provider = useWatch({ name: 'type' });
  const url = useWatch({ name: 'url' });
  const synchronizeAfterCreation = useWatch({
    name: 'synchronizeAfterCreation',
  });

  const getProviderOptions = () => {
    return [
      {
        label: t(
          'pageAutomations.calendarTools.calendarImportSection.selectProvider',
        ),
        value: '',
      },
      ...channelToProviderArray,
      {
        label: t(
          'pageAutomations.calendarTools.calendarImportSection.otherProvider',
        ),
        value: ICalType.DEFAULT,
      },
    ];
  };

  const isSubmitDisabled = !property || !provider || !url;
  const isOtherProviderSelected = provider === ICalType.DEFAULT;

  return (
    <CalendarImportFieldsContainer>
      {isFetchingProperties ? (
        <>
          <LoadingPlaceholder linesCount={1.2} width="100%" />
          <LoadingPlaceholder linesCount={1.2} width="100%" />
        </>
      ) : (
        <>
          <PropertySelectField
            name="property"
            properties={allProperties ?? []}
            emptyValueLabel={t(
              'pageAutomations.calendarTools.calendarImportSection.selectProperty',
            )}
            onChangeHandler={(e) => {
              setPropertyUid((e.target as HTMLSelectElement).value);
            }}
            enableHotels={false}
            enableUnitTypes={false}
            colSmInput={12}
          />

          <Row>
            <Col sm={3}>
              <SelectField
                colSmInput={12}
                name="type"
                options={getProviderOptions()}
              />
            </Col>
            <Col sm={6}>
              <Row>
                {isOtherProviderSelected && (
                  <Col sm={5}>
                    <TextField
                      colSmInput={12}
                      name="name"
                      placeholder={t(
                        'pageAutomations.calendarTools.calendarImportSection.inputProviderName',
                      )}
                    />
                  </Col>
                )}

                <Col sm={isOtherProviderSelected ? 7 : 12}>
                  <TextField
                    colSmInput={12}
                    name="url"
                    placeholder={t(
                      'pageAutomations.calendarTools.calendarImportSection.importUrl',
                    )}
                    leftAddon={['https://']}
                  />
                </Col>
              </Row>
              <CheckboxField name="synchronizeAfterCreation">
                {t(
                  'pageAutomations.calendarTools.calendarImportSection.keepCalendarSynced',
                )}
              </CheckboxField>
            </Col>
            <Col sm={3}>
              <Button
                type="submit"
                disabled={isSubmitDisabled}
                bsStyle="primary"
                data-testid="start-synchronization-button"
              >
                {synchronizeAfterCreation
                  ? t(
                      'pageAutomations.calendarTools.calendarImportSection.startSynchronization',
                    )
                  : t(
                      'pageAutomations.calendarTools.calendarImportSection.import',
                    )}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </CalendarImportFieldsContainer>
  );
};

export default CalendarImportFields;
